import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';

import { IAddToCalendar } from './add.to.calendar.interface';

@Component({
    selector: 'mlp-ng-add-to-calendar',
    templateUrl: './add.to.calendar.component.html',
    styleUrls: ['./add.to.calendar.component.scss']
})

export class AddToCalendarSharedComponent implements OnInit {
    @Input() event: IAddToCalendar;
    calendars;
    open = false;
    MS_IN_MINUTES = 60 * 1000;

    constructor(private sanitizer: DomSanitizer) {}

    ngOnInit(): void {
        this.calendars = this.generateCalendars({
            title: (this.event.title || ''),
            start: this.event.start,
            duration: this.event.duration,
            address: (this.event.address || ''),
            description: (this.event.description || '')
        });
    }

    sanitize(url: string): SafeUrl {
        return this.sanitizer.bypassSecurityTrustUrl(url);
    }

    formatTime(date): string {
        return date.toISOString().replace(/-|:|\.\d+/g, '');
    }

    calculateEndTime(event): string {
        return event.end ? this.formatTime(event.end) :
            this.formatTime(new Date(event.start.getTime() + (event.duration * this.MS_IN_MINUTES)));
    }

    google(event): string {
        const startTime = this.formatTime(event.start);
        const endTime = this.calculateEndTime(event);

        const href = encodeURI([
            'https://www.google.com/calendar/render',
            '?action=TEMPLATE',
            `&text=${event.title}`,
            `&dates=${startTime}/${endTime}`,
            `&details=${event.description}`,
            `&location=${event.address}`,
            '&sprop=&sprop=name:'
        ].join(''));

        return href;
    }

    yahoo(event): string {
        const eventDuration = event.end ?
        ((event.end.getTime() - event.start.getTime()) / this.MS_IN_MINUTES) :
        event.duration;

        // Yahoo dates are crazy, we need to convert the duration from minutes to hh:mm
        const yahooHourDuration = eventDuration < 600 ? `0 ${Math.floor((eventDuration / 60))}` :
            `${Math.floor((eventDuration / 60))}`;

        const yahooMinuteDuration = eventDuration % 60 < 10 ? `0 ${eventDuration % 60}` : `${eventDuration % 60}`;

        const yahooEventDuration = yahooHourDuration + yahooMinuteDuration;

        // Remove timezone from event time
        const st = this.formatTime(new Date(event.start - (event.start.getTimezoneOffset() *
                                                this.MS_IN_MINUTES))) || '';

        const href = encodeURI([
            'http://calendar.yahoo.com/?v=60&view=d&type=20',
            `&title=${event.title}`,
            `&st=${st}`,
            `&dur=${yahooEventDuration}`,
            `&desc=${event.description}`,
            `&in_loc=${event.address}`
        ].join(''));

        return href;
    }

    ics(event): string {
        const startTime = this.formatTime(event.start);
        const endTime = this.calculateEndTime(event);

        const href = encodeURI(
            'data:text/calendar;charset=utf8,' + [
            'BEGIN:VCALENDAR',
            'VERSION:2.0',
            'BEGIN:VEVENT',
            'URL:' + document.URL,
            'DTSTART:' + startTime,
            'DTEND:' + endTime,
            'SUMMARY:' + event.title,
            'DESCRIPTION:' + event.description,
            'LOCATION:' + event.address,
            'END:VEVENT',
            'END:VCALENDAR'].join('\n'));

        return href;
    }

    ical(event): string {
        return this.ics(event);
    }

    outlook(event): string {
        return this.ics(event);
    }

    generateCalendars(event): object {
        return {
            google: this.google(event),
            yahoo: this.yahoo(event),
            ical: this.ical(event),
            outlook: this.outlook(event)
        };
    }
}
